import React, { useState, useEffect, useRef } from 'react';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Alert, Badge } from 'react-bootstrap';
import { FaMapMarkerAlt, FaUserFriends, FaEdit, FaTrashAlt, FaCrosshairs, FaSearch, FaTimes, FaFileExcel, FaUnlink } from 'react-icons/fa';
import { sitesAPI, personnelAPI, branchesAPI, regionsAPI } from '../../utils/api';
import notificationService from '../../services/notificationService';
import { jwtDecode } from 'jwt-decode';
import { currentUserHasPermission } from '../../utils/permissionUtils';
import { validateGPSCoordinates, getCurrentLocation, reverseGeocode, decimalToDMS } from '../../utils/gpsUtils';
import './SiteManagement.css';


const SiteManagement = () => {
  const [sites, setSites] = useState([]);
  const [branches, setBranches] = useState([]);
  const [regions, setRegions] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);

  const [selectedSite, setSelectedSite] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    contactPersonName: '',
    contactPhone: '',
    contactDesignation: '',
    description: '',
    longitude: '',
    latitude: '',
    geofenceRadius: 10,
    locationAccuracy: 'high',
    dmsLatitude: '',
    dmsLongitude: '',
    branchId: '',
    regionId: ''
  });
  const [locationInputMethod, setLocationInputMethod] = useState('search'); // 'search', 'gps', 'decimal'
  const [gpsInput, setGpsInput] = useState('');
  const [decimalInput, setDecimalInput] = useState('');
  const [isValidatingGPS, setIsValidatingGPS] = useState(false);
  const [isCapturingLocation, setIsCapturingLocation] = useState(false);
  const [capturedCoords, setCapturedCoords] = useState(null);
  const [assignedPersonnel, setAssignedPersonnel] = useState([]);
  const [filter, setFilter] = useState('');
  const [selectedPersonnelFilter, setSelectedPersonnelFilter] = useState('');
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');
  const [selectedRegionFilter, setSelectedRegionFilter] = useState('');
  const [filteredRegionsForFilter, setFilteredRegionsForFilter] = useState([]);
  const [assignmentStatus, setAssignmentStatus] = useState('unassigned'); // 'assigned' or 'unassigned'
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isMapReady, setIsMapReady] = useState(false);
  const [isEditMapReady, setIsEditMapReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const mapRef = useRef(null);
  const editMapRef = useRef(null);

  let permissions = [];

  try {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user'));

    if (token) {
      const decoded = jwtDecode(token);
      const rawPermissions = decoded?.permissions;

      if (typeof rawPermissions === 'string') {
        permissions = JSON.parse(rawPermissions);
      } else if (Array.isArray(rawPermissions)) {
        permissions = rawPermissions;
      }
    } else if (user?.permissions) {
      permissions = Array.isArray(user.permissions)
        ? user.permissions
        : JSON.parse(user.permissions);
    }
  } catch (err) {
    console.warn('Could not parse permissions:', err);
    permissions = [];
  }

  const hasPermission = (perm) => permissions.includes(perm);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sitesResponse, personnelResponse, branchesResponse, regionsResponse] = await Promise.all([
          sitesAPI.getAll(),
          personnelAPI.getAll(),
          branchesAPI.getAll(),
          regionsAPI.getAll()
        ]);
        // Process sites to add assignedCount from assignedPersonnel array
        // Process sites to calculate effective assigned count (Explicit + Inherited)
        const allPersonnel = personnelResponse.data;
        const processedSites = sitesResponse.data.map(site => {
          const explicitIds = site.assignedPersonnel ? site.assignedPersonnel.map(p => p.id) : [];

          // Count inherited personnel
          const inheritedIds = allPersonnel
            .filter(p => {
              // Inherited from Branch
              if (p.branches && p.branches.some(b => b.id === site.branchId)) return true;
              // Inherited from Region
              if (site.regionId && p.regions && p.regions.some(r => r.id === site.regionId)) return true;
              return false;
            })
            .map(p => p.id);

          // Merge unique IDs
          const uniqueCount = new Set([...explicitIds, ...inheritedIds]).size;

          return {
            ...site,
            assignedCount: uniqueCount
          };
        });
        setSites(processedSites);
        setPersonnel(personnelResponse.data);
        setBranches(branchesResponse.data);
        setRegions(regionsResponse.data);
      } catch (err) {
        console.error("Failed to fetch data:", err);
        setError("Failed to load data. Please try again.");
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!showAddModal) return;

    let isComponentMounted = true;
    let apiLoader = null;
    let scriptElement = null;

    const currentMapRef = mapRef.current;
    if (currentMapRef) {
      const mapContainer = currentMapRef.querySelector('#map-container');
      if (mapContainer && currentMapRef.contains(mapContainer)) {
        currentMapRef.removeChild(mapContainer);
      }
    }

    const loadGMP = () => {
      const existingScript = document.querySelector('script[src*="@googlemaps/extended-component-library"]');
      if (existingScript) {
        initializeMap();
        return;
      }

      scriptElement = document.createElement('script');
      scriptElement.type = 'module';
      scriptElement.src = 'https://unpkg.com/@googlemaps/extended-component-library@0.6';
      scriptElement.onload = () => {
        if (!isComponentMounted) return;
        initializeMap();
      };
      scriptElement.onerror = () => {
        if (!isComponentMounted) return;
        console.error('Failed to load Google Maps Extended Component Library');
        setError('Failed to load map components. Please try again.');
      };
      document.head.appendChild(scriptElement);
    };

    const initializeMap = () => {
      if (!isComponentMounted || !currentMapRef) return;

      const existingApiLoader = document.querySelector('gmpx-api-loader');
      if (!existingApiLoader) {
        apiLoader = document.createElement('gmpx-api-loader');
        apiLoader.setAttribute('key', 'AIzaSyCm_5BujVh9fZNy10-gJgHq2rTblBfyjw0');
        apiLoader.setAttribute('solution-channel', 'GMP_CCS_autocomplete_v3');
        document.body.appendChild(apiLoader);
      }

      setTimeout(() => {
        if (!isComponentMounted || !currentMapRef) return;

        const mapContainer = document.createElement('div');
        mapContainer.id = 'map-container';
        mapContainer.innerHTML = `
        <gmp-map id="map" center="-1.2921,36.8219" zoom="10" map-id="DEMO_MAP_ID">
          <div slot="control-block-start-inline-start" class="pac-card">
            <div>
              <div id="title">Search location</div>
              <div id="type-selector" class="pac-controls">
                <input type="radio" name="type" id="changetype-all" checked="checked" />
                <label for="changetype-all">All</label>
                <input type="radio" name="type" id="changetype-establishment" />
                <label for="changetype-establishment">Establishment</label>
                <input type="radio" name="type" id="changetype-address" />
                <label for="changetype-address">Address</label>
                <input type="radio" name="type" id="changetype-geocode" />
                <label for="changetype-geocode">Geocode</label>
              </div>
              <br />
              <div id="strict-bounds-selector" class="pac-controls">
                <input type="checkbox" id="use-strict-bounds" value="" />
                <label for="use-strict-bounds">Restrict to map viewport</label>
              </div>
            </div>
            <gmpx-place-picker id="place-picker" for-map="map"></gmpx-place-picker>
          </div>

          <gmp-advanced-marker id="marker"></gmp-advanced-marker>
          <gmp-circle id="geofence-circle" center="-1.2921,36.8219" radius="100" stroke-color="#4285F4" stroke-opacity="0.8" stroke-weight="2" fill-color="#4285F4" fill-opacity="0.1"></gmp-circle>
        </gmp-map>
        <div id="infowindow-content" style="display: none;">
          <span id="place-name" class="title" style="font-weight: bold;"></span><br />
          <span id="place-address"></span>
        </div>
      `;


        currentMapRef.appendChild(mapContainer);

        Promise.all([
          customElements.whenDefined('gmp-map'),
          customElements.whenDefined('gmpx-place-picker'),
          customElements.whenDefined('gmp-advanced-marker')
        ]).then(() => {
          if (!isComponentMounted) return;
          setTimeout(() => {
            if (!isComponentMounted) return;
            setupMapInteractions();
          }, 500);
        }).catch((error) => {
          console.error('Error defining custom elements:', error);
          if (isComponentMounted) {
            setError('Failed to initialize map components. Please try again.');
          }
        });
      }, 100);
    };

    const setupMapInteractions = () => {
      try {
        const placePicker = document.getElementById('place-picker');
        const infowindowContent = document.getElementById('infowindow-content');

        if (!placePicker || !infowindowContent) {
          console.warn('Map elements not found');
          return;
        }

        const handlePlaceChange = () => {
          try {
            const place = placePicker.value;
            if (!place?.location) return;
            updateLocationFromPlace(place);
          } catch (error) {
            console.error('Error handling place change:', error);
          }
        };

        placePicker.addEventListener('gmpx-placechange', handlePlaceChange);

        const setupRadioButton = (id, type) => {
          const radio = document.getElementById(id);
          if (radio) {
            radio.addEventListener('click', () => {
              if (placePicker) placePicker.type = type;
            });
          }
        };

        setupRadioButton('changetype-all', '');
        setupRadioButton('changetype-establishment', 'establishment');
        setupRadioButton('changetype-address', 'address');
        setupRadioButton('changetype-geocode', 'geocode');

        const strictBoundsCheckbox = document.getElementById('use-strict-bounds');
        if (strictBoundsCheckbox) {
          strictBoundsCheckbox.addEventListener('change', () => {
            if (placePicker) {
              placePicker.strictBounds = strictBoundsCheckbox.checked;
            }
          });
        }

        setIsMapReady(true);
      } catch (error) {
        console.error('Error setting up map interactions:', error);
        setError('Failed to setup map interactions. Please try again.');
      }
    };

    if (!window.google || !document.querySelector('gmpx-api-loader')) {
      loadGMP();
    } else {
      initializeMap();
    }

    return () => {
      isComponentMounted = false;
      const currentMapRef = mapRef.current;
      if (currentMapRef) {
        currentMapRef.innerHTML = '';
      }
    };
  }, [showAddModal]);

  useEffect(() => {
    if (!showEditModal) return;

    let isComponentMounted = true;
    let apiLoader = null;
    let scriptElement = null;

    const currentMapRef = editMapRef.current;
    if (currentMapRef) {
      const mapContainer = currentMapRef.querySelector('#edit-map-container');
      if (mapContainer && currentMapRef.contains(mapContainer)) {
        currentMapRef.removeChild(mapContainer);
      }
    }

    const loadGMP = () => {
      const existingScript = document.querySelector('script[src*="@googlemaps/extended-component-library"]');
      if (existingScript) {
        initializeMap();
        return;
      }

      scriptElement = document.createElement('script');
      scriptElement.type = 'module';
      scriptElement.src = 'https://unpkg.com/@googlemaps/extended-component-library@0.6';
      scriptElement.onload = () => {
        if (!isComponentMounted) return;
        initializeMap();
      };
      scriptElement.onerror = () => {
        if (!isComponentMounted) return;
        console.error('Failed to load Google Maps Extended Component Library');
        setError('Failed to load map components. Please try again.');
      };
      document.head.appendChild(scriptElement);
    };

    const initializeMap = () => {
      if (!isComponentMounted || !currentMapRef) return;

      const existingApiLoader = document.querySelector('gmpx-api-loader');
      if (!existingApiLoader) {
        apiLoader = document.createElement('gmpx-api-loader');
        apiLoader.setAttribute('key', 'AIzaSyCm_5BujVh9fZNy10-gJgHq2rTblBfyjw0');
        apiLoader.setAttribute('solution-channel', 'GMP_CCS_autocomplete_v3');
        document.body.appendChild(apiLoader);
      }

      setTimeout(() => {
        if (!isComponentMounted || !currentMapRef) return;

        const mapContainer = document.createElement('div');
        mapContainer.id = 'edit-map-container';
        const centerLat = formData.latitude || -1.2921;
        const centerLng = formData.longitude || 36.8219;
        mapContainer.innerHTML = `
        <gmp-map id="edit-map" center="${centerLat},${centerLng}" zoom="12" map-id="DEMO_MAP_ID">
          <div slot="control-block-start-inline-start" class="pac-card">
            <div>
              <div id="edit-title">Search location</div>
              <div id="edit-type-selector" class="pac-controls">
                <input type="radio" name="edit-type" id="edit-changetype-all" checked="checked" />
                <label for="edit-changetype-all">All</label>
                <input type="radio" name="edit-type" id="edit-changetype-establishment" />
                <label for="edit-changetype-establishment">Establishment</label>
                <input type="radio" name="edit-type" id="edit-changetype-address" />
                <label for="edit-changetype-address">Address</label>
                <input type="radio" name="edit-type" id="edit-changetype-geocode" />
                <label for="edit-changetype-geocode">Geocode</label>
              </div>
              <br />
              <div id="edit-strict-bounds-selector" class="pac-controls">
                <input type="checkbox" id="edit-use-strict-bounds" value="" />
                <label for="edit-use-strict-bounds">Restrict to map viewport</label>
              </div>
            </div>
            <gmpx-place-picker id="edit-place-picker" for-map="edit-map"></gmpx-place-picker>
          </div>
          <gmp-advanced-marker id="edit-marker"></gmp-advanced-marker>
        </gmp-map>
      `;

        currentMapRef.appendChild(mapContainer);

        Promise.all([
          customElements.whenDefined('gmp-map'),
          customElements.whenDefined('gmpx-place-picker'),
          customElements.whenDefined('gmp-advanced-marker')
        ]).then(() => {
          if (!isComponentMounted) return;
          setTimeout(() => {
            if (!isComponentMounted) return;
            setupMapInteractions();
          }, 500);
        }).catch((error) => {
          console.error('Error defining custom elements:', error);
          if (isComponentMounted) {
            setError('Failed to initialize map components. Please try again.');
          }
        });
      }, 100);
    };

    const setupMapInteractions = () => {
      try {
        const placePicker = document.getElementById('edit-place-picker');
        if (!placePicker) return;

        const handlePlaceChange = () => {
          try {
            const place = placePicker.value;
            if (!place?.location) return;
            updateLocationFromPlace(place);
          } catch (error) {
            console.error('Error handling edit place change:', error);
          }
        };

        placePicker.addEventListener('gmpx-placechange', handlePlaceChange);

        const setupRadioButton = (id, type) => {
          const radio = document.getElementById(id);
          if (radio) {
            radio.addEventListener('click', () => {
              if (placePicker) placePicker.type = type;
            });
          }
        };

        setupRadioButton('edit-changetype-all', '');
        setupRadioButton('edit-changetype-establishment', 'establishment');
        setupRadioButton('edit-changetype-address', 'address');
        setupRadioButton('edit-changetype-geocode', 'geocode');

        const strictBoundsCheckbox = document.getElementById('edit-use-strict-bounds');
        if (strictBoundsCheckbox) {
          strictBoundsCheckbox.addEventListener('change', () => {
            if (placePicker) {
              placePicker.strictBounds = strictBoundsCheckbox.checked;
            }
          });
        }

        setIsEditMapReady(true);
      } catch (error) {
        console.error('Error setting up edit map interactions:', error);
        setError('Failed to setup map interactions. Please try again.');
      }
    };

    if (!window.google || !document.querySelector('gmpx-api-loader')) {
      loadGMP();
    } else {
      initializeMap();
    }

    return () => {
      isComponentMounted = false;
      const currentMapRef = editMapRef.current;
      if (currentMapRef) {
        currentMapRef.innerHTML = '';
      }
    };
  }, [showEditModal]);

  const updateLocationFromPlace = (place) => {
    if (!place?.location) return;

    try {
      let address = '';
      let city = '';
      let state = '';
      let zipCode = '';

      if (place.addressComponents && Array.isArray(place.addressComponents)) {
        place.addressComponents.forEach(component => {
          if (!component.types || !Array.isArray(component.types)) return;

          if (component.types.includes('street_number') || component.types.includes('route')) {
            address += (component.longText || component.shortText || '') + ' ';
          }
          if (component.types.includes('locality')) {
            city = component.longText || component.shortText || '';
          }
          if (component.types.includes('administrative_area_level_1')) {
            state = component.longText || component.shortText || '';
          }
          if (component.types.includes('postal_code')) {
            zipCode = component.longText || component.shortText || '';
          }
        });
      }

      const newLatitude = parseFloat(place.location.lat?.()) || '';
      const newLongitude = parseFloat(place.location.lng?.()) || '';

      setFormData(prev => ({
        ...prev,
        address: address.trim() || place.formattedAddress || prev.address,
        city: city || prev.city,
        state: state || prev.state,
        zipCode: zipCode || prev.zipCode,
        longitude: newLongitude,
        latitude: newLatitude
      }));

      // Update geofence circle on map
      if (newLatitude && newLongitude) {
        updateGeofenceCircle(newLatitude, newLongitude, formData.geofenceRadius);
      }

    } catch (error) {
      console.error('Error updating location from place:', error);
      setError('Error processing selected location. Please try again.');
    }
  };

  const updateGeofenceCircle = (latitude, longitude, radius = 100) => {
    try {
      const circle = document.getElementById('geofence-circle');
      if (circle) {
        circle.setAttribute('center', `${latitude},${longitude}`);
        circle.setAttribute('radius', radius);
      }
    } catch (error) {
      console.error('Error updating geofence circle:', error);
    }
  };

  const handleCaptureCurrentLocation = async () => {
    setIsCapturingLocation(true);
    setError('');
    try {
      const coords = await getCurrentLocation();
      setCapturedCoords(coords);

      // Format for the selected input method
      if (locationInputMethod === 'gps') {
        const dmsLat = decimalToDMS(coords.latitude, true);
        const dmsLng = decimalToDMS(coords.longitude, false);
        setGpsInput(`${dmsLat} ${dmsLng}`);
      } else {
        setDecimalInput(`${coords.latitude.toFixed(6)}, ${coords.longitude.toFixed(6)}`);
      }
    } catch (err) {
      setError(err.message || 'Failed to capture location');
    } finally {
      setIsCapturingLocation(false);
    }
  };

  const handleGPSValidation = async () => {
    const input = locationInputMethod === 'gps' ? gpsInput : decimalInput;

    if (!input.trim()) {
      setError('Please enter GPS coordinates');
      return;
    }

    setIsValidatingGPS(true);
    setError('');

    try {
      // First validate format on frontend
      const validation = validateGPSCoordinates(input);

      if (!validation.isValid) {
        setError(validation.error);
        return;
      }

      // Then validate with backend and get address info
      const response = await sitesAPI.validateGPS(input);

      if (response.data) {
        const { coordinates, addressInfo, addressError, dmsLatitude, dmsLongitude } = response.data;


        // console.log('GPS Validation Response:', response.data);


        setFormData(prev => ({
          ...prev,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          dmsLatitude: dmsLatitude,
          dmsLongitude: dmsLongitude,
          address: addressInfo?.address || addressInfo?.formattedAddress || prev.address,
          city: addressInfo?.city || prev.city,
          state: addressInfo?.state || prev.state,
          zipCode: addressInfo?.zipCode || prev.zipCode
        }));

        // Update geofence circle on map
        updateGeofenceCircle(coordinates.latitude, coordinates.longitude, formData.geofenceRadius);

        if (addressInfo) {
          setSuccess('GPS coordinates validated successfully! Address information retrieved.');
        } else {
          setSuccess('GPS coordinates validated! Note: Address lookup unavailable - please enter address manually.');
        }
        setTimeout(() => setSuccess(''), 5000);
      }
    } catch (error) {
      console.error('GPS validation error:', error);
      setError(error.response?.data?.error || 'Failed to validate GPS coordinates');
    } finally {
      setIsValidatingGPS(false);
    }
  };

  const filteredSites = sites.filter(site => {
    // "All Unassigned" overrides every other filter - show sites with zero assigned personnel
    if (showUnassignedOnly) {
      return (site.assignedCount || 0) === 0;
    }

    // Text search filter
    const matchesTextSearch = !filter ||
      site.name?.toLowerCase().includes(filter.toLowerCase()) ||
      site.address?.toLowerCase().includes(filter.toLowerCase()) ||
      site.city?.toLowerCase().includes(filter.toLowerCase()) ||
      site.state?.toLowerCase().includes(filter.toLowerCase());

    if (!matchesTextSearch) return false;

    // Branch filter
    const matchesBranch = !selectedBranchFilter || 
      site.branchId === Number(selectedBranchFilter);

    if (!matchesBranch) return false;

    // Region filter
    const matchesRegion = !selectedRegionFilter || 
      site.regionId === Number(selectedRegionFilter);

    if (!matchesRegion) return false;

    // Personnel filter - show sites based on assignment status (region-scoped)
    if (selectedPersonnelFilter) {
      const selectedPerson = personnel.find(p => p.id === Number(selectedPersonnelFilter));
      
      if (selectedPerson) {
        // Check if personnel is directly assigned to this site
        const isDirectlyAssignedToSite = site.assignedPersonnel && 
          site.assignedPersonnel.some(p => p.id === selectedPerson.id);

        // For direct assignment to count, site must be in personnel's region scope
        // OR personnel has no region restrictions
        const personRegionIds = selectedPerson.regions?.map(r => r.id) || [];
        const hasRegionRestrictions = personRegionIds.length > 0;
        const siteInPersonnelRegion = !hasRegionRestrictions || 
          (site.regionId && personRegionIds.includes(site.regionId));

        // Direct assignment only counts if site is in personnel's region scope
        const isDirectlyAssigned = isDirectlyAssignedToSite && siteInPersonnelRegion;

        // Check if personnel is inherited from branch
        const isInheritedFromBranch = selectedPerson.branches && 
          selectedPerson.branches.some(b => b.id === site.branchId);

        // Check if personnel is inherited from region
        const isInheritedFromRegion = site.regionId && 
          selectedPerson.regions && 
          selectedPerson.regions.some(r => r.id === site.regionId);

        // Personnel is assigned if any of the above is true
        const isAssigned = isDirectlyAssigned || isInheritedFromBranch || isInheritedFromRegion;

        // Return based on assignment status filter
        if (assignmentStatus === 'assigned') {
          return isAssigned;
        } else {
          return !isAssigned;
        }
      }
    }

    return true;
  });


  // Filter regions when branch changes
  useEffect(() => {
    if (formData.branchId) {
      const branchRegions = regions.filter(r => r.branchId === Number(formData.branchId));
      setFilteredRegions(branchRegions);
    } else {
      setFilteredRegions([]);
    }
  }, [formData.branchId, regions]);

  // Filter regions for the filter controls when branch filter changes
  useEffect(() => {
    if (selectedBranchFilter) {
      const branchRegions = regions.filter(r => r.branchId === Number(selectedBranchFilter));
      setFilteredRegionsForFilter(branchRegions);
      // Reset region filter if it doesn't belong to the selected branch
      if (selectedRegionFilter && !branchRegions.find(r => r.id === Number(selectedRegionFilter))) {
        setSelectedRegionFilter('');
      }
    } else {
      setFilteredRegionsForFilter([]);
      setSelectedRegionFilter('');
    }
  }, [selectedBranchFilter, regions, selectedRegionFilter]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Update geofence circle when radius changes
    if (name === 'geofenceRadius' && formData.latitude && formData.longitude) {
      updateGeofenceCircle(formData.latitude, formData.longitude, parseInt(value) || 100);
    }

  };

  const handleAddSite = async (e) => {
    e.preventDefault();
    if (!formData.longitude || !formData.latitude) {
      setError("Please select a location from the map search box or enter coordinates.");
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const payload = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };
      const response = await sitesAPI.create(payload);
      setSites([...sites, { ...response.data, assignedCount: 0 }]);
      notificationService.triggerSiteAction('site-added', response.data.name || formData.name);
      setShowAddModal(false);
      setSuccess('Site created successfully!');
      setFormData({
        name: '', address: '', city: '', state: '', zipCode: '',
        contactPersonName: '', contactPhone: '', contactDesignation: '',
        description: '', longitude: '', latitude: '',
        geofenceRadius: 100, locationAccuracy: 'high',
        dmsLatitude: '', dmsLongitude: '', branchId: '', regionId: ''
      });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error("Failed to create site:", err);
      setError(err.response?.data?.message || "Failed to create site. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditSite = (site) => {
    setSelectedSite(site);
    setFormData({
      name: site.name,
      address: site.address,
      city: site.city,
      state: site.state,
      zipCode: site.zipCode,
      contactPersonName: site.contactPersonName || '',
      contactPhone: site.contactPhone || '',
      contactDesignation: site.contactDesignation || '',
      description: site.description,
      longitude: site.longitude,
      latitude: site.latitude,
      geofenceRadius: site.geofenceRadius || 100,
      locationAccuracy: site.locationAccuracy || 'high',
      dmsLatitude: site.dmsLatitude || '',
      dmsLongitude: site.dmsLongitude || '',
      branchId: site.branchId || '',
      regionId: site.regionId || ''
    });

    setLocationInputMethod('search'); // Reset to search method
    setGpsInput(''); // Clear GPS input
    setDecimalInput(''); // Clear decimal input
    setError(''); // Clear any previous errors

    setShowEditModal(true);
  };

  const handleUpdateSite = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setError('');
    try {
      const payload = {
        ...formData,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };

      const response = await sitesAPI.update(selectedSite.id, payload);

      setSites(prevSites =>
        prevSites.map(site =>
          site.id === selectedSite.id
            ? { ...response.data.site, assignedCount: site.assignedCount }
            : site
        )
      );

      notificationService.triggerSiteAction('site-updated', formData.name || selectedSite.name);
      setShowEditModal(false);
      setSuccess('Site updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error("Failed to update site:", err);
      setError(err.response?.data?.message || "Failed to update site. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = (site) => {
    setSelectedSite(site);
    setShowDeleteModal(true);
  };

  const handleDeleteSite = async () => {
    setIsLoading(true);
    setError('');
    try {
      const siteName = selectedSite.name;
      await sitesAPI.delete(selectedSite.id);

      // Remove the site directly from the state
      setSites(prevSites => prevSites.filter(site => site.id !== selectedSite.id));

      notificationService.triggerSiteAction('site-deleted', siteName);
      setShowDeleteModal(false);
      setSuccess('Site deleted successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to delete site:", error);
      setError("Failed to delete site. Please try again.");
      setShowDeleteModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnassignClick = (site) => {
    setSelectedSite(site);
    setError('');
    setShowUnassignModal(true);
  };

  const handleUnassignSite = async () => {
    setIsLoading(true);
    setError('');
    try {
      await sitesAPI.update(selectedSite.id, { branchId: '', regionId: '' });

      setSites(prevSites =>
        prevSites.map(site =>
          site.id === selectedSite.id
            ? { ...site, branchId: null, regionId: null, branchDetails: null, regionDetails: null }
            : site
        )
      );

      notificationService.triggerSiteAction('site-updated', selectedSite.name);
      setShowUnassignModal(false);
      setSuccess('Site unassigned successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to unassign site:", error);
      setError(error.response?.data?.message || "Failed to unassign site. Please try again.");
      setShowUnassignModal(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssignClick = async (site) => {
    setSelectedSite(site);

    setError(''); // Clear any previous errors


    try {
      const response = await sitesAPI.getPersonnel(site.id);
      const explicitIds = response.data.map(p => p.id);

      // Also identify inherited personnel
      const inheritedIds = personnel
        .filter(p => {
          // Inherited from Branch
          if (p.branches && p.branches.some(b => b.id === site.branchId)) return true;
          // Inherited from Region
          if (site.regionId && p.regions && p.regions.some(r => r.id === site.regionId)) return true;
          return false;
        })
        .map(p => p.id);

      // Merge unique IDs
      const allAssignedIds = [...new Set([...explicitIds, ...inheritedIds])];
      setAssignedPersonnel(allAssignedIds);
      setShowAssignModal(true);
    } catch (error) {
      console.error("Failed to fetch assigned personnel:", error);
      setError("Failed to load assigned personnel. Please try again.");
    }
  };

  const handleAssignmentChange = (personnelId) => {
    setAssignedPersonnel(prev =>
      prev.includes(personnelId)
        ? prev.filter(id => id !== personnelId)
        : [...prev, personnelId]
    );
  };

  const handleSaveAssignments = async () => {
    setIsLoading(true);
    setError('');
    try {

      // Filter out inherited personnel before saving
      // We only want to save explicit assignments to this site
      const inheritedIds = personnel
        .filter(p => {
          if (p.branches && p.branches.some(b => b.id === selectedSite.branchId)) return true;
          if (selectedSite.regionId && p.regions && p.regions.some(r => r.id === selectedSite.regionId)) return true;
          return false;
        })
        .map(p => p.id);

      const explicitAssignments = assignedPersonnel.filter(id => !inheritedIds.includes(id));

      console.log("Saving assigned personnel:", explicitAssignments);
      await sitesAPI.assignPersonnel(selectedSite.id, explicitAssignments);

      // Update the site's assigned count directly in the state
      setSites(prevSites =>
        prevSites.map(site =>
          site.id === selectedSite.id
            ? { ...site, assignedCount: assignedPersonnel.length }
            : site
        )
      );

      setShowAssignModal(false);
      setSuccess('Personnel assignments updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error("Failed to save assignments:", error);
      setError("Failed to update personnel assignments. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions for clearing individual filters
  const clearPersonnelFilter = () => {
    setSelectedPersonnelFilter('');
    setAssignmentStatus('unassigned');
  };

  const clearBranchFilter = () => {
    setSelectedBranchFilter('');
    setSelectedRegionFilter('');
  };

  const clearRegionFilter = () => {
    setSelectedRegionFilter('');
  };

  const clearSearchFilter = () => {
    setFilter('');
  };

  const clearUnassignedOnlyFilter = () => {
    setShowUnassignedOnly(false);
  };

  // "All Unassigned" is independent of every other filter - selecting it resets
  // the rest of the filter state (including the Personnel selector) so it always
  // shows the complete list of sites with no personnel assigned.
  const handleShowAllUnassigned = () => {
    setShowUnassignedOnly(prev => {
      const next = !prev;
      if (next) {
        setFilter('');
        setSelectedPersonnelFilter('');
        setSelectedBranchFilter('');
        setSelectedRegionFilter('');
        setAssignmentStatus('unassigned');
      }
      return next;
    });
  };

  const hasActiveFilters = () => {
    return filter || selectedPersonnelFilter || selectedBranchFilter || selectedRegionFilter || showUnassignedOnly;
  };

  const handleExportToExcel = async () => {
    setIsLoading(true);
    setError('');
    try {
      const filterParams = {
        search: filter || undefined,
        personnelId: selectedPersonnelFilter || undefined,
        assignmentStatus: selectedPersonnelFilter ? assignmentStatus : undefined,
        branchId: selectedBranchFilter || undefined,
        regionId: selectedRegionFilter || undefined,
        unassignedOnly: showUnassignedOnly || undefined
      };

      // Remove undefined values
      const cleanParams = Object.fromEntries(
        Object.entries(filterParams).filter(([_, v]) => v !== undefined)
      );

      const response = await sitesAPI.exportFiltered(cleanParams);
      
      // Create blob from response
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filenamePrefix = showUnassignedOnly ? 'all-unassigned-sites-export' : 'sites-export';
      link.download = `${filenamePrefix}-${timestamp}.xlsx`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSuccess('Sites exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Export failed:', error);
      setError(error.response?.data?.message || 'Failed to export sites. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };




  return (
    <Container fluid>
      <style>{`
        #map-container, #edit-map-container {
          height: 400px;
          margin-bottom: 1rem;
          position: relative;
        }
        gmp-map {
          height: 100%;
          width: 100%;
        }
        .pac-card {
          background-color: #fff;
          border-radius: 4px;
          box-shadow: 0 1px 4px -1px rgba(0, 0, 0, 0.3);
          margin: 10px;
          font: 400 14px Roboto, Arial, sans-serif;
          overflow: hidden;
          width: calc(100% - 20px);
        }
        #title, #edit-title {
          color: #fff;
          background-color: #4d90fe;
          font-size: 16px;
          font-weight: 500;
          padding: 6px 12px;
        }
        .pac-controls {
          display: inline-block;
          padding: 5px 11px;
        }
        .pac-controls label {
          font-size: 13px;
          font-weight: 300;
          margin-right: 10px;
        }
        #place-picker, #edit-place-picker {
          box-sizing: border-box;
          width: 100%;
          padding: 0.5rem 1rem 1rem;
        }
        gmp-map:not(:defined) {
          display: none;
        }
        
        /* Filter UI Styling */
        .search-input-wrapper {
          position: relative;
        }
        .search-input-wrapper .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6c757d;
          pointer-events: none;
        }
        .search-input-wrapper input {
          padding-left: 38px;
        }
        
        .segmented-toggle {
          display: inline-flex;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          overflow: hidden;
          background: #fff;
        }
        .segmented-toggle.disabled {
          opacity: 0.5;
          pointer-events: none;
        }
        .segmented-toggle button {
          padding: 8px 24px;
          border: none;
          background: transparent;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
          color: #495057;
        }
        .segmented-toggle button:not(:last-child) {
          border-right: 1px solid #dee2e6;
        }
        .segmented-toggle button:hover:not(.active) {
          background: #f8f9fa;
        }
        .segmented-toggle button.active {
          background: #0d6efd;
          color: white;
        }

        .all-unassigned-btn {
          padding: 8px 24px;
          border: 1px solid #0d6efd;
          border-radius: 6px;
          background: #fff;
          color: #0d6efd;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .all-unassigned-btn:hover:not(.active) {
          background: #f8f9fa;
        }
        .all-unassigned-btn.active {
          background: #0d6efd;
          color: white;
        }

        .filter-chip {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 12px;
          background: #e7f3ff;
          border: 1px solid #b6d4fe;
          border-radius: 20px;
          font-size: 14px;
          margin-right: 8px;
          margin-bottom: 8px;
        }
        .filter-chip .chip-label {
          font-weight: 600;
          color: #0a58ca;
        }
        .filter-chip .chip-value {
          color: #495057;
        }
        .filter-chip .chip-close {
          cursor: pointer;
          color: #6c757d;
          transition: color 0.2s;
          font-size: 12px;
        }
        .filter-chip .chip-close:hover {
          color: #dc3545;
        }
      `}</style>

      <h1 className="mb-4">Site Management</h1>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          {/* Main Filter Row */}
          <Row className="mb-3 align-items-end">
            <Col md={3}>
              <Form.Group>
                <Form.Label>Search by Name/Address</Form.Label>
                <div className="search-input-wrapper">
                  <FaSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Search sites..."
                    value={filter}
                    onChange={(e) => {
                      setFilter(e.target.value);
                      setShowUnassignedOnly(false);
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Personnel</Form.Label>
                <Form.Select
                  value={selectedPersonnelFilter}
                  onChange={(e) => {
                    setSelectedPersonnelFilter(e.target.value);
                    setShowUnassignedOnly(false);
                  }}
                >
                  <option value="">All Personnel</option>
                  {personnel.map(person => (
                    <option key={person.id} value={person.id}>
                      {person.name} ({person.position})
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Branch</Form.Label>
                <Form.Select
                  value={selectedBranchFilter}
                  onChange={(e) => {
                    setSelectedBranchFilter(e.target.value);
                    setShowUnassignedOnly(false);
                  }}
                >
                  <option value="">All Branches</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Region</Form.Label>
                <Form.Select
                  value={selectedRegionFilter}
                  onChange={(e) => {
                    setSelectedRegionFilter(e.target.value);
                    setShowUnassignedOnly(false);
                  }}
                  disabled={!selectedBranchFilter}
                >
                  <option value="">All Regions</option>
                  {filteredRegionsForFilter.map(region => (
                    <option key={region.id} value={region.id}>{region.name}</option>
                  ))}
                </Form.Select>
                {!selectedBranchFilter && (
                  <Form.Text className="text-muted d-block">
                    Select a branch first
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          </Row>

          {/* Assignment Status Toggle & Clear Filters Row */}
          <Row className="mb-3 align-items-center">
            <Col md={6}>
              <div className="d-flex align-items-center gap-3">
                <span style={{ fontSize: '14px', color: '#6c757d', fontWeight: '500' }}>Assignment Status:</span>
                <button
                  type="button"
                  className={`all-unassigned-btn ${showUnassignedOnly ? 'active' : ''}`}
                  onClick={handleShowAllUnassigned}
                  title="Show all sites with no personnel assigned, regardless of other filters"
                >
                  All Unassigned
                </button>
                <div className={`segmented-toggle ${!selectedPersonnelFilter ? 'disabled' : ''}`}>
                  <button
                    className={assignmentStatus === 'unassigned' ? 'active' : ''}
                    onClick={() => {
                      setAssignmentStatus('unassigned');
                      setShowUnassignedOnly(false);
                    }}
                  >
                    Unassigned
                  </button>
                  <button
                    className={assignmentStatus === 'assigned' ? 'active' : ''}
                    onClick={() => {
                      setAssignmentStatus('assigned');
                      setShowUnassignedOnly(false);
                    }}
                  >
                    Assigned
                  </button>
                </div>
                {!selectedPersonnelFilter && (
                  <span style={{ fontSize: '13px', color: '#6c757d', fontStyle: 'italic' }}>
                    Select personnel to filter
                  </span>
                )}
              </div>
            </Col>
            <Col md={6} className="text-end">
              {hasActiveFilters() && (
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="success"
                    size="sm"
                    onClick={handleExportToExcel}
                    disabled={isLoading}
                  >
                    <FaFileExcel className="me-2" />
                    {isLoading ? 'Exporting...' : 'Export to Excel'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setFilter('');
                      setSelectedPersonnelFilter('');
                      setSelectedBranchFilter('');
                      setSelectedRegionFilter('');
                      setAssignmentStatus('unassigned');
                      setShowUnassignedOnly(false);
                    }}
                  >
                    <FaTimes className="me-2" />
                    Clear All Filters
                  </Button>
                </div>
              )}
            </Col>
          </Row>

          {/* Active Filter Chips */}
          {hasActiveFilters() && (
            <Row className="mb-3">
              <Col>
                <div className="d-flex flex-wrap">
                  {filter && (
                    <div className="filter-chip">
                      <span className="chip-label">Search:</span>
                      <span className="chip-value">{filter}</span>
                      <FaTimes className="chip-close" onClick={clearSearchFilter} />
                    </div>
                  )}
                  {selectedPersonnelFilter && (
                    <div className="filter-chip">
                      <span className="chip-label">Personnel:</span>
                      <span className="chip-value">
                        {personnel.find(p => p.id === Number(selectedPersonnelFilter))?.name}
                        {' '}({assignmentStatus === 'assigned' ? 'Assigned' : 'Unassigned'})
                      </span>
                      <FaTimes className="chip-close" onClick={clearPersonnelFilter} />
                    </div>
                  )}
                  {selectedBranchFilter && (
                    <div className="filter-chip">
                      <span className="chip-label">Branch:</span>
                      <span className="chip-value">
                        {branches.find(b => b.id === Number(selectedBranchFilter))?.name}
                      </span>
                      <FaTimes className="chip-close" onClick={clearBranchFilter} />
                    </div>
                  )}
                  {selectedRegionFilter && (
                    <div className="filter-chip">
                      <span className="chip-label">Region:</span>
                      <span className="chip-value">
                        {regions.find(r => r.id === Number(selectedRegionFilter))?.name}
                      </span>
                      <FaTimes className="chip-close" onClick={clearRegionFilter} />
                    </div>
                  )}
                  {showUnassignedOnly && (
                    <div className="filter-chip">
                      <span className="chip-label">Showing:</span>
                      <span className="chip-value">All Unassigned Sites</span>
                      <FaTimes className="chip-close" onClick={clearUnassignedOnlyFilter} />
                    </div>
                  )}
                </div>
              </Col>
            </Row>
          )}

          {/* Add New Site Button */}
          <div className="d-flex justify-content-end mb-3">
            <Button
              variant="primary"
              onClick={() => {
                setError('');
                setShowAddModal(true);
              }}
            >
              <FaMapMarkerAlt className="me-2" />
              Add New Site
            </Button>
          </div>

          <Table responsive hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Address</th>
                <th>City</th>
                <th>State</th>
                <th>Assigned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSites.length > 0 ? (
                (() => {
                  const sitesByBranch = filteredSites.reduce((acc, site) => {
                    const branchName = site.branchDetails ? site.branchDetails.name : 'Unassigned Branch';
                    const regionName = site.regionDetails ? site.regionDetails.name : 'Unassigned Region';

                    if (!acc[branchName]) {
                      acc[branchName] = {};
                    }
                    if (!acc[branchName][regionName]) {
                      acc[branchName][regionName] = [];
                    }
                    acc[branchName][regionName].push(site);
                    return acc;
                  }, {});

                  const sortedBranches = Object.keys(sitesByBranch).sort((a, b) => {
                    if (a === 'Unassigned Branch') return 1;
                    if (b === 'Unassigned Branch') return -1;
                    return a.localeCompare(b);
                  });

                  return sortedBranches.map(branchName => {
                    const regions = sitesByBranch[branchName];
                    const sortedRegions = Object.keys(regions).sort((a, b) => {
                      if (a === 'Unassigned Region') return 1;
                      if (b === 'Unassigned Region') return -1;
                      return a.localeCompare(b);
                    });

                    return (
                      <React.Fragment key={branchName}>
                        {/* Branch Header */}
                        <tr className="branch-header-row">
                          <td colSpan="6" style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                            {branchName}
                          </td>
                        </tr>

                        {sortedRegions.map(regionName => (
                          <React.Fragment key={`${branchName}-${regionName}`}>
                            {/* Region Header - Only show if it's not the only "Unassigned" default group or if we want to show structure explicitly. 
                                 Let's show it to be clear, maybe with indentation. */}
                            <tr className="region-header-row">
                              <td colSpan="6" style={{ paddingLeft: '2rem', fontStyle: 'italic' }}>
                                <span style={{ color: '#666' }}>Region:</span> {regionName}
                              </td>
                            </tr>

                            {regions[regionName].map(site => (
                              <tr key={site.id}>
                                <td style={{ paddingLeft: '3rem' }}>{site.name}</td>
                                <td>{site.address}</td>
                                <td>{site.city}</td>
                                <td>{site.state}</td>
                                <td>{site.assignedCount || 0}</td>
                                <td>
                                  <Button variant="outline-primary" size="sm" className="me-2"
                                    onClick={() => handleAssignClick(site)}>
                                    <FaUserFriends />
                                  </Button>
                                  <Button variant="outline-secondary" size="sm" className="me-2"
                                    onClick={() => handleEditSite(site)}>
                                    <FaEdit />
                                  </Button>
                                  <Button variant="outline-warning" size="sm" className="me-2 unassign-action-btn"
                                    disabled={!(site.branchId && site.regionId)}
                                    title={!(site.branchId && site.regionId) ? 'Site is already unassigned' : 'Unassign branch and region'}
                                    onClick={() => handleUnassignClick(site)}>
                                    <FaUnlink />
                                  </Button>
                                  <Button variant="outline-danger" size="sm"
                                    onClick={() => handleDeleteClick(site)}>
                                    <FaTrashAlt />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    {showUnassignedOnly
                      ? 'No unassigned sites found'
                      : (filter ? 'No matching sites found' : 'No sites available')}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>


      {/* Add Site Modal */}

      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add New Site</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          <Form onSubmit={handleAddSite}>
            <Form.Group className="mb-3">

              <Form.Label>Site Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">

              <Form.Label>Location Input Method</Form.Label>
              <div className="mb-3">
                <Form.Check
                  type="radio"
                  id="search-method"
                  name="locationMethod"
                  label="Search by Address"
                  checked={locationInputMethod === 'search'}
                  onChange={() => setLocationInputMethod('search')}
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  id="gps-method"
                  name="locationMethod"
                  label="Enter GPS Coordinates (DMS)"
                  checked={locationInputMethod === 'gps'}
                  onChange={() => setLocationInputMethod('gps')}
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  id="decimal-method"
                  name="locationMethod"
                  label="Enter Decimal Coordinates"
                  checked={locationInputMethod === 'decimal'}
                  onChange={() => setLocationInputMethod('decimal')}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Branch</Form.Label>
              <Form.Select
                name="branchId"
                value={formData.branchId}
                onChange={handleInputChange}
              >
                <option value="">Select a branch...</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Region</Form.Label>
              <Form.Select
                name="regionId"
                value={formData.regionId}
                onChange={handleInputChange}
                disabled={!formData.branchId}
              >
                <option value="">Select a region...</option>
                {filteredRegions.map(region => (
                  <option key={region.id} value={region.id}>{region.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {locationInputMethod === 'search' && (
              <Form.Group className="mb-3">
                <Form.Label>Map Selection</Form.Label>
                <div ref={mapRef} style={{ height: '400px' }}>
                  {!isMapReady && (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading map...</span>
                      </div>
                    </div>
                  )}
                </div>
              </Form.Group>
            )}

            {locationInputMethod === 'gps' && (
              <Form.Group className="mb-3">
                <Form.Label>GPS Coordinates (DMS Format)</Form.Label>
                <div className="site-coord-input-wrapper">
                  <button
                    type="button"
                    className="site-coord-location-btn"
                    onClick={handleCaptureCurrentLocation}
                    disabled={isCapturingLocation}
                    title="Capture current location"
                  >
                    {isCapturingLocation ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <FaMapMarkerAlt />
                    )}
                  </button>
                  <Form.Control
                    type="text"
                    className="site-coord-input"
                    placeholder="Example: 1°06'50.0'S 36°44'34.8'E or click icon"
                    value={gpsInput}
                    onChange={(e) => setGpsInput(e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Enter coordinates in Degrees, Minutes, Seconds format, or click the icon to capture your current location
                </Form.Text>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={handleGPSValidation}
                >
                  <FaCrosshairs className="me-1" />
                  {isValidatingGPS ? 'Validating...' : 'Validate & Get Address'}
                </Button>
              </Form.Group>
            )}

            {locationInputMethod === 'decimal' && (
              <Form.Group className="mb-3">
                <Form.Label>Decimal Coordinates</Form.Label>
                <div className="site-coord-input-wrapper">
                  <button
                    type="button"
                    className="site-coord-location-btn"
                    onClick={handleCaptureCurrentLocation}
                    disabled={isCapturingLocation}
                    title="Capture current location"
                  >
                    {isCapturingLocation ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <FaMapMarkerAlt />
                    )}
                  </button>
                  <Form.Control
                    type="text"
                    className="site-coord-input"
                    placeholder="Example: -1.1139, 36.7430 or click icon"
                    value={decimalInput}
                    onChange={(e) => setDecimalInput(e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Enter coordinates as latitude, longitude in decimal degrees, or click the icon to capture your current location
                </Form.Text>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={handleGPSValidation}
                >
                  <FaCrosshairs className="me-1" />
                  {isValidatingGPS ? 'Validating...' : 'Validate & Get Address'}
                </Button>
              </Form.Group>
            )}







            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </Form.Group>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Person Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactPersonName"
                    value={formData.contactPersonName}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Designation</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactDesignation"
                    value={formData.contactDesignation}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-4" />
            <h5>Geofence Configuration</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Geofence Radius (meters)</Form.Label>
                  <Form.Control
                    type="number"
                    name="geofenceRadius"
                    value={10}
                    readOnly
                    disabled
                  />
                  <Form.Text className="text-muted">
                    Distance in meters for location verification (fixed: 10m)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location Accuracy</Form.Label>
                  <Form.Select
                    name="locationAccuracy"
                    value="high"
                    disabled
                    readOnly
                  >
                    <option value="high">High (GPS accurate)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Required accuracy level for check-in verification (fixed: High)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {(formData.dmsLatitude || formData.dmsLongitude) && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>DMS Latitude</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.dmsLatitude}
                      readOnly
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>DMS Longitude</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.dmsLongitude}
                      readOnly
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
              >
                {isLoading ? 'Adding...' : 'Add Site'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Site</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          <Form onSubmit={handleUpdateSite}>
            <Form.Group className="mb-3">
              <Form.Label>Site Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Location Input Method</Form.Label>
              <div className="mb-3">
                <Form.Check
                  type="radio"
                  id="edit-search-method"
                  name="editLocationMethod"
                  label="Search by Address"
                  checked={locationInputMethod === 'search'}
                  onChange={() => setLocationInputMethod('search')}
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  id="edit-gps-method"
                  name="editLocationMethod"
                  label="Enter GPS Coordinates (DMS)"
                  checked={locationInputMethod === 'gps'}
                  onChange={() => setLocationInputMethod('gps')}
                  className="mb-2"
                />
                <Form.Check
                  type="radio"
                  id="edit-decimal-method"
                  name="editLocationMethod"
                  label="Enter Decimal Coordinates"
                  checked={locationInputMethod === 'decimal'}
                  onChange={() => setLocationInputMethod('decimal')}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Branch</Form.Label>
              <Form.Select
                name="branchId"
                value={formData.branchId}
                onChange={handleInputChange}
              >
                <option value="">Select a branch...</option>
                {branches.map(branch => (
                  <option key={branch.id} value={branch.id}>{branch.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Region</Form.Label>
              <Form.Select
                name="regionId"
                value={formData.regionId}
                onChange={handleInputChange}
                disabled={!formData.branchId}
              >
                <option value="">Select a region...</option>
                {filteredRegions.map(region => (
                  <option key={region.id} value={region.id}>{region.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            {locationInputMethod === 'search' && (
              <Form.Group className="mb-3">
                <Form.Label>Map Selection</Form.Label>
                <div ref={editMapRef} style={{ height: '400px' }}>
                  {!isEditMapReady && (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '100%' }}>
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading map...</span>
                      </div>
                    </div>
                  )}
                </div>
              </Form.Group>
            )}

            {locationInputMethod === 'gps' && (
              <Form.Group className="mb-3">
                <Form.Label>GPS Coordinates (DMS Format)</Form.Label>
                <div className="site-coord-input-wrapper">
                  <button
                    type="button"
                    className="site-coord-location-btn"
                    onClick={handleCaptureCurrentLocation}
                    disabled={isCapturingLocation}
                    title="Capture current location"
                  >
                    {isCapturingLocation ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <FaMapMarkerAlt />
                    )}
                  </button>
                  <Form.Control
                    type="text"
                    className="site-coord-input"
                    placeholder="Example: 1°06'50.0'S 36°44'34.8'E or click icon"
                    value={gpsInput}
                    onChange={(e) => setGpsInput(e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Enter coordinates in Degrees, Minutes, Seconds format, or click the icon to capture your current location
                </Form.Text>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={handleGPSValidation}
                  disabled={isValidatingGPS || !gpsInput.trim()}
                >
                  <FaCrosshairs className="me-1" />
                  {isValidatingGPS ? 'Validating...' : 'Validate & Get Address'}
                </Button>
              </Form.Group>
            )}

            {locationInputMethod === 'decimal' && (
              <Form.Group className="mb-3">
                <Form.Label>Decimal Coordinates</Form.Label>
                <div className="site-coord-input-wrapper">
                  <button
                    type="button"
                    className="site-coord-location-btn"
                    onClick={handleCaptureCurrentLocation}
                    disabled={isCapturingLocation}
                    title="Capture current location"
                  >
                    {isCapturingLocation ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <FaMapMarkerAlt />
                    )}
                  </button>
                  <Form.Control
                    type="text"
                    className="site-coord-input"
                    placeholder="Example: -1.1139, 36.7430 or click icon"
                    value={decimalInput}
                    onChange={(e) => setDecimalInput(e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  Enter coordinates as latitude, longitude in decimal degrees, or click the icon to capture your current location
                </Form.Text>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="mt-2"
                  onClick={handleGPSValidation}
                  disabled={isValidatingGPS || !decimalInput.trim()}
                >
                  <FaCrosshairs className="me-1" />
                  {isValidatingGPS ? 'Validating...' : 'Validate & Get Address'}
                </Button>
              </Form.Group>
            )}


            <Form.Group className="mb-3">
              <Form.Label>Address</Form.Label>
              <Form.Control
                type="text"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Zip Code</Form.Label>
                  <Form.Control
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Person Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactPersonName"
                    value={formData.contactPersonName}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactPhone"
                    value={formData.contactPhone}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Designation</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactDesignation"
                    value={formData.contactDesignation}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <hr className="my-4" />
            <h5>Geofence Configuration</h5>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Geofence Radius (meters)</Form.Label>
                  <Form.Control
                    type="number"
                    name="geofenceRadius"
                    value={10}
                    readOnly
                    disabled
                  />
                  <Form.Text className="text-muted">
                    Distance in meters for location verification (fixed: 10m)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Location Accuracy</Form.Label>
                  <Form.Select
                    name="locationAccuracy"
                    value="high"
                    disabled
                    readOnly
                  >
                    <option value="high">High (GPS accurate)</option>
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Required accuracy level for check-in verification (fixed: High)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {(formData.dmsLatitude || formData.dmsLongitude) && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>DMS Latitude</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.dmsLatitude}
                      readOnly
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>DMS Longitude</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.dmsLongitude}
                      readOnly
                      className="bg-light"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            <div className="d-flex justify-content-end">
              <Button variant="secondary" className="me-2" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                title={
                  !currentUserHasPermission('sites', 'edit')
                    ? 'You do not have permission to update sites'
                    : ''
                }
              >
                {isLoading ? 'Updating...' : 'Update Site'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Assign Personnel to {selectedSite?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
          {personnel.length > 0 ? (
            <Form>
              <div className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="select-all"
                  label="Select All"
                  onChange={() => {
                    if (assignedPersonnel.length === personnel.length) {
                      setAssignedPersonnel([]);
                    } else {
                      setAssignedPersonnel(personnel.map(p => p.id));
                    }
                  }}
                  checked={assignedPersonnel.length === personnel.length && personnel.length > 0}
                />
              </div>

              <div className="personnel-list" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {personnel.map(person => {
                  let isInherited = false;
                  let inheritanceLabel = '';

                  if (selectedSite) {
                    // Inherited from Branch
                    if (person.branches && person.branches.some(b => b.id === selectedSite.branchId)) {
                      isInherited = true;
                      inheritanceLabel = 'Inherited (Branch)';
                    }
                    // Inherited from Region
                    else if (selectedSite.regionId && person.regions && person.regions.some(r => r.id === selectedSite.regionId)) {
                      isInherited = true;
                      inheritanceLabel = 'Inherited (Region)';
                    }
                  }

                  return (
                    <div key={person.id} className="d-flex align-items-center mb-2">
                      <Form.Check
                        type="checkbox"
                        id={`person-${person.id}`}
                        checked={assignedPersonnel.includes(person.id)}
                        disabled={isInherited}
                        onChange={() => handleAssignmentChange(person.id)}
                        className="me-2"
                      />
                      <label htmlFor={`person-${person.id}`} className="mb-0" style={{ cursor: isInherited ? 'default' : 'pointer' }}>
                        {person.name} ({person.position})
                        {isInherited && (
                          <Badge bg="info" className="ms-2">{inheritanceLabel}</Badge>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            </Form>
          ) : (
            <Alert variant="info">No personnel available for assignment. Add personnel first.</Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAssignModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAssignments}
            title={
              !currentUserHasPermission('sites', 'assign')
                ? 'You do not have permission to assign personnel'
                : ''
            }
          >
            {isLoading ? 'Saving...' : 'Save Assignments'}
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Delete Confirmation Modal */}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the site <strong>{selectedSite?.name}</strong>? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteSite}
            title={
              !currentUserHasPermission('sites', 'delete')
                ? 'You do not have permission to delete sites'
                : ''
            }
          >
            {isLoading ? 'Deleting...' : 'Delete Site'}
          </Button>
        </Modal.Footer>
      </Modal>


      {/* Unassign Confirmation Modal */}

      <Modal show={showUnassignModal} onHide={() => setShowUnassignModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Unassign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to unassign the branch and region from <strong>{selectedSite?.name}</strong>? This site will return to an unassigned state.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowUnassignModal(false)}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={handleUnassignSite}
            disabled={!currentUserHasPermission('sites', 'edit')}
            title={
              !currentUserHasPermission('sites', 'edit')
                ? 'You do not have permission to edit sites'
                : ''
            }
          >
            {isLoading ? 'Unassigning...' : 'Unassign Site'}
          </Button>
        </Modal.Footer>
      </Modal>


    </Container>
  );
};

export default SiteManagement;
